import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlay,
  faPause,
  faExpand,
  faVolumeHigh,
  faVolumeXmark,
  faXmark,
  faVideoSlash,
  faFire,
} from '@fortawesome/free-solid-svg-icons';
import { api } from '../lib/api';

interface VideoItem {
  id: number;
  title: string;
  description?: string;
  video_url: string | null;
  poster_url: string | null;
  status: 'active' | 'closed';
  viewer_count: number;
  created_at: string;
}

function getSessionId(): string {
  let sid = sessionStorage.getItem('_video_sid');
  if (!sid) {
    sid = crypto.randomUUID();
    sessionStorage.setItem('_video_sid', sid);
  }
  return sid;
}

function getBrowserInfo(): Record<string, string> {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const tzCountryMap: Record<string, string> = {
      'Asia/Shanghai': 'CN', 'Asia/Chongqing': 'CN', 'Asia/Harbin': 'CN', 'Asia/Urumqi': 'CN',
      'Asia/Hong_Kong': 'HK', 'Asia/Macau': 'MO', 'Asia/Taipei': 'TW',
    };
    const country = tzCountryMap[tz] || '';
    return {
      screen_resolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language || '',
      timezone: tz,
      country,
    };
  } catch {
    return {};
  }
}

function trackEvent(videoId: number, eventType: string, eventData?: Record<string, any>) {
  api
    .post('/video-streams/track', {
      video_id: videoId,
      session_id: getSessionId(),
      event_type: eventType,
      event_data: eventData ?? null,
      browser_info: getBrowserInfo(),
    })
    .catch(() => { });
}

export default function VideoPage() {
  const [videos, setVideos] = useState<VideoItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeVideo, setActiveVideo] = useState<VideoItem | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);
  const durationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [viewerCount, setViewerCount] = useState(0);
  const viewerIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    api
      .get('/video-streams')
      .then(({ data }) => {
        setVideos(Array.isArray(data) ? data : []);
      })
      .catch(() => setVideos([]))
      .finally(() => setLoading(false));
  }, []);

  const startDurationTracking = useCallback(
    (videoId: number) => {
      if (durationIntervalRef.current) clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = setInterval(() => {
        if (videoRef.current && !videoRef.current.paused) {
          trackEvent(videoId, 'duration', {
            current_time: Math.floor(videoRef.current.currentTime),
            total_duration: Math.floor(videoRef.current.duration || 0),
          });
        }
      }, 10000);
    },
    []
  );

  const stopDurationTracking = useCallback(() => {
    if (durationIntervalRef.current) {
      clearInterval(durationIntervalRef.current);
      durationIntervalRef.current = null;
    }
  }, []);

  const openVideo = (video: VideoItem) => {
    if (video.status === 'closed') return;
    setActiveVideo(video);
    setIsPlaying(false);
    setProgress(0);
    setCurrentTime(0);
    setDuration(0);
    trackEvent(video.id, 'view');

    setViewerCount(video.viewer_count || 0);
    api.post(`/video-streams/${video.id}/view`)
      .then(({ data }) => setViewerCount(data.viewer_count))
      .catch(() => { });

    if (viewerIntervalRef.current) clearInterval(viewerIntervalRef.current);
    viewerIntervalRef.current = setInterval(() => {
      api.post(`/video-streams/${video.id}/view`)
        .then(({ data }) => setViewerCount(data.viewer_count))
        .catch(() => { });
    }, 15000);
  };

  const closeVideo = () => {
    stopDurationTracking();
    if (viewerIntervalRef.current) {
      clearInterval(viewerIntervalRef.current);
      viewerIntervalRef.current = null;
    }
    setActiveVideo(null);
    setIsPlaying(false);
  };

  const togglePlay = () => {
    if (!videoRef.current || !activeVideo) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setIsPlaying(true);
      trackEvent(activeVideo.id, 'play', { current_time: Math.floor(videoRef.current.currentTime) });
      startDurationTracking(activeVideo.id);
    } else {
      videoRef.current.pause();
      setIsPlaying(false);
      trackEvent(activeVideo.id, 'pause', { current_time: Math.floor(videoRef.current.currentTime) });
      stopDurationTracking();
    }
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setIsMuted(videoRef.current.muted);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    const ct = videoRef.current.currentTime;
    const dur = videoRef.current.duration || 1;
    setProgress((ct / dur) * 100);
    setCurrentTime(ct);
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) setDuration(videoRef.current.duration);
  };

  const handleEnded = () => {
    if (activeVideo) {
      trackEvent(activeVideo.id, 'complete', { total_duration: Math.floor(duration) });
    }
    stopDurationTracking();
    setIsPlaying(false);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pct * (videoRef.current.duration || 0);
  };

  const handleFullscreen = () => {
    if (videoRef.current) {
      if (videoRef.current.requestFullscreen) {
        videoRef.current.requestFullscreen();
      } else if ((videoRef.current as any).webkitEnterFullscreen) {
        (videoRef.current as any).webkitEnterFullscreen();
      }
    }
  };

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  };

  const formatDateChinese = (dateStr: string) => {
    const d = new Date(dateStr);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    return `${year}年${month}月${day}日 ${hours}:${minutes}`;
  };

  useEffect(() => {
    return () => {
      stopDurationTracking();
      if (viewerIntervalRef.current) clearInterval(viewerIntervalRef.current);
    };
  }, [stopDurationTracking]);

  return (
    <div
      className="max-w-md mx-auto min-h-screen flex flex-col pb-16"
      style={{
        backgroundImage: 'url(/images/video_beijing.png)',
        backgroundSize: '100% auto',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'top center',
        backgroundColor: '#9a1215',
      }}
    >
      {/* Spacer for the decorative header baked into background image */}
      <div style={{ paddingTop: '180px' }} />

      {/* Video cards list */}
      <div className="px-3 space-y-3 flex-1">
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl bg-white/20 animate-pulse h-[110px]" />
            ))}
          </div>
        )}

        {!loading && videos.length === 0 && (
          <div className="text-center py-12 px-4">
            <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-white/20 flex items-center justify-center">
              <FontAwesomeIcon icon={faVideoSlash} className="text-white/50 text-xl" />
            </div>
            <p className="text-white/60 text-sm">暂无会议播报</p>
          </div>
        )}

        {!loading && videos.map((video, index) => (
          <div
            key={video.id}
            className={`relative transition-transform ${video.status === 'closed' ? 'opacity-70' : 'cursor-pointer active:scale-[0.98]'}`}
            onClick={() => openVideo(video)}
          >
            <div
              className="rounded-2xl overflow-hidden p-2.5"
              style={{
                backgroundImage: 'url(/images/video_kuang.png)',
                backgroundSize: '100% 100%',
                backgroundPosition: 'top center'
              }}
            >
              <div className="flex gap-3">
                {/* Thumbnail */}
                <div className="w-[38%] relative flex-shrink-0 rounded-xl overflow-hidden">
                  {video.poster_url ? (
                    <img
                      src={video.poster_url}
                      alt={video.title}
                      className={`w-full aspect-[4/3] object-cover ${video.status === 'closed' ? 'grayscale' : ''}`}
                    />
                  ) : (
                    <div className="w-full aspect-[4/3] bg-gradient-to-br from-gray-600 to-gray-800" />
                  )}
                  {video.status === 'active' && (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-10 h-10 rounded-full bg-black/30 border-2 border-white/80 flex items-center justify-center">
                        <FontAwesomeIcon icon={faPlay} className="text-white text-sm ml-0.5" />
                      </div>
                    </div>
                  )}
                  {video.description && (
                    <div className="absolute bottom-1.5 left-1.5">
                      <span className="text-white text-[11px] font-medium drop-shadow-lg">
                        {video.description}
                      </span>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 flex flex-col justify-between py-0.5 min-w-0">
                  <h3 className="text-[15px] font-bold text-[#333] leading-snug line-clamp-2">
                    {video.title}
                  </h3>
                  <div className="space-y-2 mt-auto">
                    <div className="flex items-center gap-1.5">
                      <img src="/images/video_shijian.png" alt="" className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="text-[#999] text-[11px]">
                        {formatDateChinese(video.created_at)}
                      </span>
                    </div>
                    <div className="flex justify-end">
                      <div
                        className="h-7 px-4 flex items-center justify-center rounded-full"
                        style={{
                          backgroundImage: 'url(/images/video_guankan.png)',
                          backgroundSize: '100% 100%',
                          backgroundRepeat: 'no-repeat',
                        }}
                      >
                        <span className="text-white text-[12px] font-medium">立即观看</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* "最新" badge on first item */}
            {index === 0 && (
              <img
                src="/images/video_zuixin.png"
                alt="最新"
                className="absolute -top-1 -right-1 w-12 h-12 pointer-events-none"
              />
            )}
          </div>
        ))}
      </div>

      <div className="h-4" />

      {/* Video Player Modal */}
      {activeVideo && (
        <div className="fixed inset-0 z-50 bg-black flex flex-col">
          <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/80 to-transparent p-4">
            <div className="flex items-center justify-between max-w-md mx-auto">
              <button
                onClick={closeVideo}
                className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 backdrop-blur-md text-white active:bg-white/20 transition"
              >
                <FontAwesomeIcon icon={faXmark} className="text-lg" />
              </button>
              <h2 className="flex-1 text-center text-white font-semibold text-sm truncate px-3">
                {activeVideo.title}
              </h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-red-600/90 backdrop-blur-sm rounded-full shadow-lg shadow-red-500/30">
                  <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                  <span className="text-white text-xs font-bold tracking-wide">回放</span>
                </div>
                <div className="flex items-center gap-1.5 px-2.5 py-1 bg-black/40 backdrop-blur-sm rounded-full">
                  <FontAwesomeIcon icon={faFire} className="text-orange-400 text-xs" />
                  <span className="text-white text-xs font-medium">{viewerCount.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 flex items-center justify-center" onClick={togglePlay}>
            <video
              ref={videoRef}
              src={activeVideo.video_url || ''}
              poster={activeVideo.poster_url || undefined}
              className="w-full h-full object-contain"
              playsInline
              muted={isMuted}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={handleLoadedMetadata}
              onEnded={handleEnded}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            {!isPlaying && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center border border-white/10 animate-pulse">
                  <FontAwesomeIcon icon={faPlay} className="text-white text-3xl ml-1.5" />
                </div>
              </div>
            )}
          </div>

          <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent pb-safe">
            <div className="max-w-md mx-auto px-4 pb-4 pt-10">
              <div
                className="w-full h-1.5 bg-white/10 rounded-full cursor-pointer mb-3 group/bar"
                onClick={handleProgressClick}
              >
                <div
                  className="h-full bg-gradient-to-r from-red-500 to-orange-500 rounded-full relative transition-all"
                  style={{ width: `${progress}%` }}
                >
                  <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3.5 h-3.5 rounded-full bg-white shadow-lg shadow-red-500/30 opacity-0 group-hover/bar:opacity-100 transition" />
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <button onClick={togglePlay} className="text-white p-2">
                    <FontAwesomeIcon icon={isPlaying ? faPause : faPlay} className="text-lg" />
                  </button>
                  <button onClick={toggleMute} className="text-white/70 p-2">
                    <FontAwesomeIcon icon={isMuted ? faVolumeXmark : faVolumeHigh} className="text-base" />
                  </button>
                  <span className="text-white/50 text-xs font-mono">
                    {formatTime(currentTime)} / {formatTime(duration)}
                  </span>
                </div>
                <button onClick={handleFullscreen} className="text-white/70 p-2">
                  <FontAwesomeIcon icon={faExpand} className="text-base" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
